import { useCallback, useState } from 'react';
import type { ILogging } from '../../api/types/ILogging';
import { bridge } from '../../utils';
import { favoriteMarketService } from '../../api';

interface UseFavoriteMarketParams {
  sno: number;
  initialFavorited: boolean;
  initialFavoritesCount?: number;
  logging: ILogging;
}

interface UseFavoriteMarketResult {
  isFavorited: boolean;
  favoritesCount: number;
  toggleFavorite: () => void;
}

export const useFavoriteMarket = ({
  sno,
  initialFavorited,
  initialFavoritesCount = 0,
  logging,
}: UseFavoriteMarketParams): UseFavoriteMarketResult => {
  const [isFavorited, setIsFavorited] = useState(initialFavorited);
  const [favoritesCount, setFavoritesCount] = useState(initialFavoritesCount);

  const toggleFavorite = useCallback(async () => {
    const shouldFavorite = !isFavorited;

    setIsFavorited(shouldFavorite);
    setFavoritesCount((prev) =>
      shouldFavorite ? prev + 1 : Math.max(prev - 1, 0),
    );

    bridge.logAnalyticsEvent({
      name: shouldFavorite
        ? 'CLICK_FAVORITE_MARKET'
        : 'CLICK_UNFAVORITE_MARKET',
      logging,
      ably_only_log: {
        event_name: shouldFavorite ? 'favorite' : 'unfavorite',
      },
    });

    try {
      const res = shouldFavorite
        ? await favoriteMarketService.post({ sno })
        : await favoriteMarketService.delete({ sno });

      setFavoritesCount((prev) => res.data.favorite_count ?? prev);
      bridge.favoriteMarket({
        market_sno: sno,
        is_favorite: shouldFavorite,
      });
    } catch {
      setIsFavorited(!shouldFavorite);
      setFavoritesCount((prev) =>
        shouldFavorite ? Math.max(prev - 1, 0) : prev + 1,
      );
    }
  }, [isFavorited, sno, logging]);

  return { isFavorited, favoritesCount, toggleFavorite };
};
